import { useEffect, useRef, useState } from 'react';

export function useTrackAudioLevel(track?: MediaStreamTrack | null) {
  const [audioLevel, setAudioLevel] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  useEffect(() => {
    if (!track) {
      setAudioLevel(0);
      return;
    }

    // Create a new MediaStream wrapper.
    // DO NOT stop the track on cleanup, or it will kill the original audio!
    const stream = new MediaStream([track]);

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    audioContextRef.current = audioContext;

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.3;
    analyserRef.current = analyser;

    const source = audioContext.createMediaStreamSource(stream);
    sourceRef.current = source;
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const average = sum / dataArray.length;
      const normalizedLevel = average / 255;

      // Only update state if the change is significant to prevent excessive re-renders
      setAudioLevel((prev) => {
        if (Math.abs(prev - normalizedLevel) > 0.02) {
          return normalizedLevel;
        }
        return prev;
      });

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(console.error);
    }

    updateLevel();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(console.error);
      }
    };
  }, [track]);

  return audioLevel;
}
